import { NgModule } from '@angular/core';

import { AppSharedRepositoryPort } from 'application/common/port/app-shared-repository.port';
import { MessageRepositoryPort } from 'application/message/port/message-repository.port';
import { NotificationRepositoryPort } from 'application/notification/port/notification-repository.port';
import { CvTemplateRepositoryPort } from 'application/template/cv/port/cv-template-repository.port';
import { ArticleRepositoryAdapter } from 'article/article-repository.adapter';
import { ArticleRepositoryPort } from 'article/port/article-repository.port';
import { ContactRepositoryAdapter } from 'contact/contact-repository.adapter';
import { ContactRepositoryPort } from 'contact/port/contact-repository.port';
import { CvTemplateRepositoryAdapter } from 'document/cv/template/cv-template-repository-adapter.service';
import { DocumentRepositoryAdapter } from 'document/document-repository.adapter';
import { DocumentRepositoryPort } from 'document/port/document-repository.port';
import { JobOfferRepositoryAdapter } from 'job/job-repository.adapter';
import { JobOfferApplicationPort } from 'job/port/job-offer-application.port';
import { JobOfferRepositoryPort } from 'job/port/job-offer-repository.port';
import { ModuleRepositoryAdapter } from 'module/module-repository.adapter';
import { ModuleGraphRepositoryAdapter } from 'module/page/graph/module-graph-repository.adapter';
import { ModuleGraphRepositoryPort } from 'module/page/graph/port/module-graph-repository.port';
import { ModulePageRepositoryAdapter } from 'module/page/module-page-repository.adapter';
import { ModulePageRepositoryPort } from 'module/page/port/module-page-repository.port';
import { ModuleQuestionRepositoryAdapter } from 'module/page/question/module-question-repository.adapter';
import { ModuleQuestionStore } from 'module/page/question/module-question.types';
import { ModuleQuestionRepositoryPort } from 'module/page/question/port/module-question-repository.port';
import { ModuleTaskRepositoryAdapter } from 'module/page/task/module-task-repository.adapter';
import { ModuleTaskRepositoryPort } from 'module/page/task/port/module-task-repository.port';
import { ModuleApplicationPort } from 'module/port/module-application.port';
import { ModuleRepositoryPort } from 'module/port/module-repository.port';
import { OrganizationRepositoryAdapter } from 'organization/organization-repository.adapter';
import { OrganizationRepositoryPort } from 'organization/port/organization-repository.port';
import { PhaseRepositoryAdapter } from 'phase/phase-repository.adapter';
import { PhaseRepositoryPort } from 'phase/port/phase-repository.port';
import { SectionRepositoryPort } from 'phase/section/port/section-repository.port';
import { SectionRepositoryAdapter } from 'phase/section/section-repository.adapter';
import { AppSharedRepositoryAdapter } from 'repository/common/app-shared-repository.adapter';
import { MessageRepositoryAdapter } from 'repository/message/message-repository.adapter';
import { NotificationRepositoryAdapter } from 'repository/notification/notification-repository.adapter';
import { TaskRepositoryPort } from 'task/port/task-repository.port';
import { TaskRepositoryAdapter } from 'task/task-repository.adapter';
import { TaskStore } from 'task/task.store';
import { UserModuleAnswerRepositoryAdapter } from 'user/answer/user-module-answer-repository-adapter.service';
import { UserEmailModelRepositoryAdapter } from 'user/email-model/user-email-model-repository.adapter';
import { UserModuleRepositoryAdapter } from 'user/module/user-module-repository.adapter';
import { UserMoodRepositoryAdapter } from 'user/mood/user-mood-repository.adapter';
import { UserNoteRepositoryAdapter } from 'user/note/user-note-repository.adapter';
import { UserEmailModelRepositoryPort } from 'user/port/email/user-email-model-repository.port';
import { UserModuleAnswerRepositoryPort } from 'user/port/module/answer/user-module-answer-repository.port';
import { UserModuleRepositoryPort } from 'user/port/module/user-module-repository.port';
import { UserMoodRepositoryPort } from 'user/port/mood/user-mood-repository.port';
import { UserNoteRepositoryPort } from 'user/port/note/user-note-repository.port';
import { UserModuleSuggestionRepositoryPort } from 'user/port/suggestion/user-module-suggestion-repository.port';
import { UserApplicationPort } from 'user/port/user-application.port';
import { UserRepositoryPort } from 'user/port/user-repository.port';
import { UserModuleSuggestionRepositoryAdapter } from 'user/suggestion/user-module-suggestion-repository.adapter';
import { UserRepositoryAdapter } from 'user/user-repository.adapter';
import { UserStore } from 'user/user.store';

@NgModule({
	providers: [
		{
			provide: UserModuleSuggestionRepositoryPort,
			useClass: UserModuleSuggestionRepositoryAdapter,
		},
		{
			provide: NotificationRepositoryPort,
			useClass: NotificationRepositoryAdapter,
		},
		{
			provide: OrganizationRepositoryPort,
			useClass: OrganizationRepositoryAdapter,
		},
		{
			provide: ModuleGraphRepositoryPort,
			useClass: ModuleGraphRepositoryAdapter,
		},
		{
			useClass: UserEmailModelRepositoryAdapter,
			provide: UserEmailModelRepositoryPort,
		},
		{
			provide: ModuleTaskRepositoryPort,
			useClass: ModuleTaskRepositoryAdapter,
		},
		{
			provide: UserModuleAnswerRepositoryPort,
			useClass: UserModuleAnswerRepositoryAdapter,
		},
		{
			provide: UserModuleRepositoryPort,
			useClass: UserModuleRepositoryAdapter,
		},
		{
			provide: ModulePageRepositoryPort,
			useClass: ModulePageRepositoryAdapter,
		},
		{ provide: UserMoodRepositoryPort, useClass: UserMoodRepositoryAdapter },
		{ provide: AppSharedRepositoryPort, useClass: AppSharedRepositoryAdapter },
		{ provide: JobOfferRepositoryPort, useClass: JobOfferRepositoryAdapter },
		{ provide: DocumentRepositoryPort, useClass: DocumentRepositoryAdapter },
		{ provide: UserNoteRepositoryPort, useClass: UserNoteRepositoryAdapter },
		{ provide: ContactRepositoryPort, useClass: ContactRepositoryAdapter },
		{ provide: SectionRepositoryPort, useClass: SectionRepositoryAdapter },
		{ provide: MessageRepositoryPort, useClass: MessageRepositoryAdapter },
		{ provide: ArticleRepositoryPort, useClass: ArticleRepositoryAdapter },
		{ provide: ModuleRepositoryPort, useClass: ModuleRepositoryAdapter },
		{ provide: PhaseRepositoryPort, useClass: PhaseRepositoryAdapter },
		{
			provide: TaskRepositoryPort,
			deps: [TaskStore, ModuleApplicationPort, JobOfferApplicationPort],
			useFactory: (
				store: TaskStore,
				moduleApplicationPort: ModuleApplicationPort,
				jobOfferApplicationPort: JobOfferApplicationPort
			) => {
				return new TaskRepositoryAdapter(
					store,
					(ids) => moduleApplicationPort.observeAttachedModulesByIds(ids),
					(ids) => jobOfferApplicationPort.observeAttachedJobOffersByIds(ids)
				);
			},
		},
		{
			provide: ModuleQuestionRepositoryPort,
			deps: [ModuleQuestionStore, ModuleApplicationPort],
			useFactory: (store, moduleApplicationPort) => {
				return new ModuleQuestionRepositoryAdapter(store, (ids) =>
					moduleApplicationPort.observeAttachedModulesByIds(ids)
				);
			},
		},
		{
			provide: UserRepositoryPort,
			deps: [UserStore, UserApplicationPort],
			useFactory: (store, userApplicationPort) => {
				return new UserRepositoryAdapter(store, (ids) =>
					userApplicationPort.observeAttachedOrganizationsByIds(ids)
				);
			},
		},
		{
			provide: CvTemplateRepositoryPort,
			useClass: CvTemplateRepositoryAdapter,
		},
	],
})
export class AppRepositoryModule {}
